<script>
   /****************************************************************/
   /* PlotExtremes                                                 */
   /* -----------------------------------------                    */
   /* shows extremes plot for DD-SIMCA results                     */
   /****************************************************************/

   import { Vector } from 'mdatools/arrays';
   import { qnorm } from 'mdatools/distributions';
   import { Segments, Legend, Lines } from 'svelte-plots-basic/2d';
   import { LINE_COLORS, FACE_TCOLORS, MARKERS } from './Defaults.js';
   import { csvRow, downloadCSV } from '../utils.js';

   import PlotSeries from './PlotSeries.svelte';
   import Axes from './Axes.svelte';

   let {
      params,                        // JSON with main plot parameters
      props,                         // JSON with main properties (models, results, datasets, etc. formed in AppPlots)
   } = $props()

   // constants
   const ciColor = '#e0e0e0';
   const xLabel = 'Number of aliens (expected)';
   const yLabel = 'Number of aliens (observed)';
   const fileName='plot-aliens';

   // function to compute elements of confidence ellipse
   function getCI(n) {

      // here we add (0, 0) point hence n + 1
      const xStart = Vector.zeros(n + 1);
      const yStart = Vector.zeros(n + 1);
      const xEnd = Vector.zeros(n + 1);
      const yEnd = Vector.zeros(n + 1);

      for (let i = 1; i < n; i++) {
         const b = i / n;
         const em = 2 * Math.sqrt(i * (1 - b));
         xStart.v[i] = i;
         xEnd.v[i] = i;
         yStart.v[i] = i - em;
         yEnd.v[i] = i + em;
      }

      xStart.v[n] = n;
      xEnd.v[n] = n;
      yStart.v[n] = n;
      yEnd.v[n] = n;

      return {xStart, xEnd, yStart, yEnd};
   }


   // function to compute the observed extremes
   function getObserved(res, simca, ncomp) {
      const ra = res.v[ncomp - 1];
      const na = ra.rolsNum.alien;
      const y = Vector.zeros(na);
      for (let i = 1; i <= na; i++) {

         // compute expected beta
         const b = i / na;

         // based on beta compute critical value for f-distance
         const zb = qnorm(b);
         const eCrit = ra.m * Math.pow(ra.Sz * zb + ra.Mz, 1.0 / ra.hz);
         let sum = 0;
         for (let j of res.ind.strangers) {
            if (ra.rols[j] === 'alien' && ra.f[j] < eCrit) sum += 1;
         }
         y.v[i - 1] = sum;
      }
      return y;
   }

   // get the properties needed for the plot
   let res = $derived(props && props.classres ? props.classres : null);
   let simca = $derived(props && props.simca ? props.simca : null);
   let ncomp = $derived(props && props.ncomp ? props.ncomp : null);

   // get plot parameters (results to show and legend position)
   let resIDs = $derived(params.resID !== 'both' ? [params.resID] : Object.keys(res));
   let legendPosition = $derived(params.legendPosition ? params.legendPosition : 'bottomright');


   // create legend items based on resIDs array
   let legendItems = $derived(resIDs ? resIDs.map(v => ({
      id: v,
      label: v,
      point: {
         marker: 1,
         lineColor: LINE_COLORS[v],
         faceColor: FACE_TCOLORS[v],
         marker: MARKERS[v],
         markerSize: 0.85,
         lineWidth: 1.5
      },
   })) : null);

   // compute x-coordinates for each result object
   let xValues = $derived(res && resIDs ? resIDs.reduce((acc, id) => {
      const n = res[id].v[ncomp - 1].rolsNum.alien;
      // 'Vector.seq(1, 0)' is [1, 0]: no aliens, no points
      acc[id] = n > 0 ? Vector.seq(1, n) : Vector.zeros(0);
      return acc
   }, {}) : null);

   // compute y-coordinates for each result object
   let yValues = $derived(res && resIDs && ncomp ? resIDs.reduce((acc, id) => {
      acc[id] = getObserved(res[id], simca, ncomp);
      return acc;
   }, {}) : null);

   // get largest number of values in the series
   let aMax = $derived(resIDs ? resIDs.reduce((acc, id) => Math.max(acc, xValues[id].length), 0) : 1);

   // compute confidence interval
   let ci = $derived(aMax > 1 ? getCI(aMax) : null);

   // compute axis limits
   let limX = $derived(xValues ? [-1, aMax + 2] : [0, 1]);
   let limY = $derived(xValues ? [-1, aMax + 2] : [0, 1]);

   // other plot parameters
   let title = $derived(ncomp ? `Aliens (A = ${ncomp})` : 'Aliens');

   // method for creating CSV version of the plot data
   function downloadCSVCallback() {

      // 'props.data' is a map keyed by result id: the first set's separator
      const { sep, dec } = Object.values(props.data)[0];

      let x = [];
      let y = [];
      let s = [];

      for (let id of resIDs) {
         const n = xValues[id].length;
         x = [...x, ...Array.from(xValues[id].v)];
         y = [...y, ...Array.from(yValues[id].v)];
         s = [...s, ...Array(n).fill(id)];
      }

      let out = csvRow(['Set', xLabel, yLabel], sep, dec);
      for (let i = 0; i < x.length; i++) {
         out += csvRow([s[i], x[i], y[i]], sep, dec);
      }

      // no confidence interval with a single point, as on the plot
      if (ci) {
         for (let i = 0; i < ci.xEnd.length; i++) {
            out += csvRow(["ci-top", ci.xEnd.v[i], ci.yEnd.v[i]], sep, dec);
         }
         for (let i = 0; i < ci.xStart.length; i++) {
            out += csvRow(["ci-bottom", ci.xStart.v[i], ci.yStart.v[i]], sep, dec);
         }
      }


      downloadCSV(out, 'plot-aliens');
   }
</script>

{#if res }

<Axes {title} {xLabel} {yLabel} {limX} {limY} {fileName} {downloadCSVCallback} whole={true}>

   {#if ci}
   <Segments {...ci} lineColor={ciColor} />
   <Lines xValues={ci.xStart} yValues={ci.yStart} lineColor={ciColor} />
   <Lines xValues={ci.xEnd} yValues={ci.yEnd} lineColor={ciColor} />
   {/if}

   <!-- data points with statistics -->
   {#each resIDs as id, i}
      {#if xValues[id].length > 0}
      <PlotSeries
         xValues={xValues[id]}
         yValues={yValues[id]}
         plotType='p'
         showLabels={false}
         faceColor={FACE_TCOLORS[id]}
         mainColor={LINE_COLORS[id]}
         marker={MARKERS[id]}
         lineWidth={1.5}
      />
      {/if}
   {/each}


   {#if legendItems && legendItems.length > 0}
   <Legend items={legendItems} position={legendPosition} />
   {/if}
</Axes>
{/if}
